/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { set, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  UserRoleType,
  USER_ROLE_ADMIN,
  USER_ROLES,
  USER_ROLES_LABELS,
  USER_ROLE_AUTHOR,
  USER_ROLE_AUTHOR_REDACT,
  USER_ROLE_VIEWER,
  USER_ROLE_VIEWER_REDACT,
  USER_ROLES_REDACT_SUFFIX,
  USER_ROLES_REDACT_MAP,
} from 'cio/utils/user-roles';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';

import UserModel from 'cio/models/user';
import AccountService from 'cio/services/account';
import SsoService from 'cio/services/sso';
import AuthService from 'cio/services/auth';
import { hexCodeIsLight } from 'cio/utils/color-functions';
import { EMOJI_REGEX } from 'cio/utils/string';
import EnvironmentModel from 'cio/models/environment';
import isDestroyed from 'cio/utils/is-destroyed';
import { ProductApp } from 'cio/utils/product-app-info';

const WORKSPACE_CUTOFF = 5;

const Validations = {
  email(value: string) {
    if (!isValidEmail(value.trim())) {
      return 'Please enter a valid email address';
    }

    return true;
  },

  role(this: TableRow, value: string, model: UserModel) {
    let isRemovingOwnAdmin = value !== USER_ROLE_ADMIN && model.role === USER_ROLE_ADMIN;
    if (isRemovingOwnAdmin && this.args.dontAllowAdminRemoval) {
      return 'At least one user must remain an admin.';
    }

    return true;
  },
};

interface TableRowArgs {
  user: UserModel;
  currentUser: UserModel;
  resetPasswordAction: (user: UserModel) => Promise<unknown>;
  onUpdateSubmit: (user: UserModel) => Promise<unknown>;
  onDelete: (user: UserModel) => Promise<unknown>;
  environments: EnvironmentModel[];
  dontAllowAdminRemoval?: boolean;
}

type EnvironmentDetails = {
  name: string;
  initials: string;
  foregroundColor?: string;
  backgroundColor?: string;
};

type EditableUserProperty = 'firstName' | 'lastName' | 'role' | 'email';

const EDITABLE_PROPERTIES: EditableUserProperty[] = ['firstName', 'lastName', 'role', 'email'];

type EditableUser = Pick<UserModel, EditableUserProperty> & {
  accessibleEnvironments: EnvironmentModel[];
};

export default class TableRow extends Component<TableRowArgs> {
  @service auth!: AuthService;
  @service account!: AccountService;
  @service sso!: SsoService;

  _validations: FormValidations;

  @tracked _isEditing = false;
  @tracked _user!: EditableUser;

  constructor(owner: unknown, args: TableRowArgs) {
    super(owner, args);
    this.setupUser();
    this._validations = FormValidations.create({ validations: Validations, modelKey: '_user', context: this });
  }

  get isJourneysActive() {
    return this.account.accessibleProducts.includes(ProductApp.JOURNEYS);
  }

  get userRoleLabel() {
    return USER_ROLES_LABELS[this.args.user.role];
  }

  get userRolesSelectOptions() {
    return USER_ROLES.filter((r) => this.isJourneysActive || r !== USER_ROLE_AUTHOR)
      .filter((r) => ![USER_ROLE_AUTHOR_REDACT, USER_ROLE_VIEWER_REDACT].includes(r))
      .map((value) => ({
        label: USER_ROLES_LABELS[value],
        value,
      }));
  }

  @action
  setUserRoleRedact() {
    set(
      this._user,
      'role',
      USER_ROLES_REDACT_MAP[this._user.role as keyof typeof USER_ROLES_REDACT_MAP] as UserRoleType,
    );
  }

  @computed('_user.role')
  get isRedacted() {
    return [USER_ROLE_VIEWER_REDACT, USER_ROLE_AUTHOR_REDACT].includes(this._user.role as UserRoleType);
  }

  @computed('_user.role')
  get canRedact() {
    return [USER_ROLE_AUTHOR, USER_ROLE_AUTHOR_REDACT, USER_ROLE_VIEWER, USER_ROLE_VIEWER_REDACT].includes(
      this._user.role,
    );
  }

  @computed('_user.role')
  get userRole() {
    return this._user.role?.replace(USER_ROLES_REDACT_SUFFIX, '');
  }

  get canEdit(): boolean {
    if (this.isCurrentUser) {
      return true;
    }

    return this.auth.hasPermission('account/team_members/edit');
  }

  @computed('sso.ssoConfiguration.oidcGoogleHostedDomain', 'args.user.emailDomain')
  get hasGoogleSSODomainMismatch() {
    if (!this.sso.ssoConfiguration?.oidcGoogleHostedDomain) {
      return false;
    }

    return this.args.user.emailDomain !== this.sso.ssoConfiguration.oidcGoogleHostedDomain;
  }

  @computed('_user.role')
  get editingRoleIsAdmin() {
    return this._user.role === USER_ROLE_ADMIN;
  }

  @computed('_user.accessibleEnvironments.[]', 'args.user.accessibleEnvironments.[]')
  get hasUpdatedEnvironments() {
    return (
      this.args.user.accessibleEnvironments.length !== this._user.accessibleEnvironments.length ||
      !this.args.user.accessibleEnvironments.every((env) => this._user.accessibleEnvironments.includes(env))
    );
  }

  @computed(
    `args.user.{${EDITABLE_PROPERTIES.join(',')}}`,
    `_user.{${EDITABLE_PROPERTIES.join(',')}}`,
    'hasUpdatedEnvironments',
  )
  get isFormDirty() {
    return EDITABLE_PROPERTIES.any((prop) => this._user[prop] !== this.args.user[prop]) || this.hasUpdatedEnvironments;
  }

  get isCurrentUser() {
    return this.args.user === this.args.currentUser;
  }

  @computed('args.user.accessibleEnvironments.length', 'args.environments.length')
  get allSelected() {
    let { user, environments } = this.args;

    if (user.role === USER_ROLE_ADMIN) {
      return true;
    }

    return environments.every((environment) => user.canAccessEnvironment(environment.id));
  }

  @computed('args.user.accessibleEnvironments.@each.id', 'args.environments.@each.id')
  get accessibleEnvironmentDetails() {
    let { user, environments } = this.args;

    return environments
      .filter((environment) => user.canAccessEnvironment(environment.id))
      .map((environment) => {
        let details: EnvironmentDetails = {
          name: environment.name,
          initials: this.generateAcronym(environment.name),
        };

        if (!isEmpty(environment.hexCode)) {
          details.backgroundColor = environment.hexCode;
          details.foregroundColor = this.foregroundColorFor(environment.hexCode);
        }

        return details;
      });
  }

  @computed('accessibleEnvironmentDetails.[]')
  get upToFiveAccessibleEnvironmentDetails() {
    return this.accessibleEnvironmentDetails.slice(0, WORKSPACE_CUTOFF);
  }

  @computed('accessibleEnvironmentDetails.[]')
  get excessAccessibleEnvironments() {
    if (this.accessibleEnvironmentDetails.length > WORKSPACE_CUTOFF) {
      return this.accessibleEnvironmentDetails.length - WORKSPACE_CUTOFF;
    }

    return 0;
  }

  resetPasswordTask = task(async () => {
    await this.args.resetPasswordAction(this.args.user);
  });

  submitTask = task(async () => {
    await this._validations.validateModel();
    this.args.user.setProperties({ ...this._user, email: this._user.email.trim() });

    if (this.args.user.role === USER_ROLE_ADMIN) {
      this.args.user.set('accessibleEnvironments', this.args.environments);
    }

    await this.args.onUpdateSubmit(this.args.user);
    this._isEditing = false;
  });

  willDestroy() {
    super.willDestroy();

    if (this._isEditing && !isDestroyed(this.args.user)) {
      this.args.user.rollbackAttributes();
    }
  }

  @action
  setAccessibleEnvironments(environments: EnvironmentModel[]) {
    set(this._user, 'accessibleEnvironments', environments);
  }

  @action
  startEditing() {
    this.setupUser();
  }

  @action
  cancelEditing() {
    this._isEditing = false;
    this.args.user.rollbackAttributes();
    this.setupUser();
  }

  @action
  setUserRole(value: UserRoleType) {
    set(this._user, 'role', value);
    this._validations.validateAttribute('role');

    // Admins can access all environments
    if (this._user.role === USER_ROLE_ADMIN) {
      set(this._user, 'accessibleEnvironments', this.args.environments);
    }
  }

  @action
  onUserValueChange(field: keyof EditableUser, validate: boolean, value: string) {
    this._user = {
      ...this._user,
      [field]: value,
    };

    if (validate === true) {
      this._validations.validateAttribute(field);
    }
  }

  private foregroundColorFor(hex: string) {
    if (hexCodeIsLight(hex)) {
      return '#000';
    }

    return '#fff';
  }

  private generateAcronym(name: string): string {
    return name
      .replace(EMOJI_REGEX, '') // remove all emojis from string
      .replace(/[^A-Za-z0-9 ]/, '') // remove all non-alphanumeric and non-space characters
      .split(/\s/)
      .map((word) => word.slice(0, 1))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  private setupUser() {
    let { firstName, lastName, email, role, accessibleEnvironments: accessibleEnvironments } = this.args.user;

    this._user = {
      firstName,
      lastName,
      email,
      role,
      accessibleEnvironments: accessibleEnvironments.toArray(),
    };
  }
}
